import { useState, type FormEvent } from 'react';
import { darkTheme } from '../../lib/tokens';
import { ModalShell, ModalActions } from '../../components/PromptDialog';
import { estimateCostEur } from './types';

// Vergabe-Dialog für Kontingent-Grants (CUR-523).
//
// Die Kostenzeile ist kein Zierrat: sie ist die Bremse gegen den vertippten
// Nuller. Sie rechnet mit den gemessenen Stückkosten aus der Spec §6 und zeigt
// eine Spanne, weil eine Aktion Premium (Haiku) und Premium+ (Sonnet) treffen
// kann.

export interface QuotaGrantResult {
  amount: number;
  validUntil: string;
  campaign?: string;
  reason: string;
}

/** Vorschlag: Ende des nächsten Monats — „für einen Monat" mit Puffer. */
function defaultValidUntil(): string {
  const now = new Date();
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 2, 0));
  return end.toISOString().slice(0, 10);
}

export function QuotaGrantModal({
  userCount,
  onSubmit,
  onCancel,
}: {
  userCount: number;
  onSubmit: (result: QuotaGrantResult) => void;
  onCancel: () => void;
}): JSX.Element {
  const [amount, setAmount] = useState('100');
  const [validUntil, setValidUntil] = useState(defaultValidUntil());
  const [campaign, setCampaign] = useState('');
  const [reason, setReason] = useState('');

  const parsedAmount = Number.parseInt(amount, 10);
  const amountValid = Number.isInteger(parsedAmount) && parsedAmount > 0 && parsedAmount <= 1000;
  const totalUnits = amountValid ? parsedAmount * userCount : 0;
  const cost = estimateCostEur(totalUnits);
  const ready = amountValid && !!validUntil && !!reason.trim();

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!ready) return;
    // 23:59:59 lokal — ein Grant „bis zum 30.09." soll den 30.09. einschließen.
    const until = new Date(`${validUntil}T23:59:59`);
    if (Number.isNaN(until.getTime())) return;
    onSubmit({
      amount: parsedAmount,
      validUntil: until.toISOString(),
      ...(campaign.trim() ? { campaign: campaign.trim() } : {}),
      reason: reason.trim(),
    });
  }

  const inputStyle = { borderColor: darkTheme.colors.borderSubtle };
  const inputClass =
    'w-full rounded-md border bg-bg-base px-3 py-2 text-sm text-text-primary outline-none focus:border-accent';

  return (
    <ModalShell
      title={userCount === 1 ? 'Kontingent erhöhen' : `Kontingent vergeben — ${userCount} Nutzer`}
      onSubmit={handleSubmit}
    >
      <p className="mb-4 text-sm text-text-secondary">
        Zusätzliche Tutor-Nachrichten, befristet. Sie werden erst verbraucht, wenn das reguläre
        Kontingent leer ist — wer seine Monatsmenge nie ausschöpft, tastet den Bonus nie an.
      </p>

      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-text-secondary">
            Zusätzliche Nachrichten je Nutzer (max. 1.000)
          </span>
          <input
            type="number"
            min={1}
            max={1000}
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={inputClass}
            style={inputStyle}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-text-secondary">
            Gültig bis (Pflicht — einen unbefristeten Bonus gibt es nicht)
          </span>
          <input
            type="date"
            required
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            className={inputClass}
            style={inputStyle}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-text-secondary">
            Kampagne (optional — gruppiert den Aktions-Report)
          </span>
          <input
            type="text"
            value={campaign}
            onChange={(e) => setCampaign(e.target.value)}
            placeholder="z. B. sommer-2026"
            className={inputClass}
            style={inputStyle}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-text-secondary">
            Begründung (Pflicht)
          </span>
          <textarea
            required
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="z. B. Aktion: Rückgewinnung inaktiver Premium-Nutzer"
            className={inputClass}
            style={inputStyle}
          />
        </label>
      </div>

      {amountValid && (
        <p className="mt-4 rounded-md border border-border-subtle bg-bg-base px-3 py-2 text-xs text-text-secondary">
          {totalUnits.toLocaleString('de-DE')} Nachrichten insgesamt · Kosten bei voller
          Ausschöpfung{' '}
          <span className="font-semibold text-text-primary">
            {cost.min.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
          </span>{' '}
          (Premium/Haiku) bis{' '}
          <span className="font-semibold text-text-primary">
            {cost.max.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
          </span>{' '}
          (Premium+/Sonnet)
        </p>
      )}

      <ModalActions onCancel={onCancel} confirmLabel="Weiter" disabled={!ready} />
    </ModalShell>
  );
}
